import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { isStudioPreview, previewQueryParams } from "@/lib/studioPreviewBridge";
import { bootstrapBranding } from "@/lib/bootstrap";

// Identité de marque de la boutique courante (Studio de thème).
// `loaded` est false tant que l'appel réseau n'a pas répondu — les
// composants qui affichent le nom/tagline (Logo, Footer…) restent muets
// pendant ce temps pour éviter un flash du nom par défaut avant que le
// vrai nom de la boutique soit connu.
const DEFAULTS = { name: "ClickExpert", tagline: "Solution web et mobile", display_name: "", hide_name: false, logo_url: "", favicon_url: "", icons: {}, loaded: false };

/**
 * Texte à afficher À CÔTÉ DU LOGO (en-tête, pied de page) : le texte réglé
 * dans le Studio (Identité › Texte à côté du logo), sinon le nom de la
 * boutique. Le NOM de la boutique (`branding.name`) reste, lui, celui des
 * titres de page, des mentions légales et des textes alternatifs.
 */
export function brandLabel(branding) {
  return String(branding?.display_name || "").trim() || branding?.name || "";
}

// Applique la favicon de la boutique courante sur <link rel="icon"> — repli
// silencieux sur la favicon statique du site (public/favicon.ico) tant que
// la boutique n'en a pas défini une elle-même.
//
// index.html déclare plusieurs <link rel="icon"> par défaut (32x32, 16x16,
// sans taille, pour le site agence ClickExpert). Ne mettre à jour que le
// premier laissait les autres pointer vers l'icône par défaut — le
// navigateur pouvait alors choisir un de ceux-ci plutôt que celui modifié,
// donnant l'impression que la favicon de la boutique n'était pas prise en
// compte. On retire donc tous les <link rel="icon"> existants et on n'en
// recrée qu'un seul, sans attribut sizes/type qui ne correspondrait pas au
// fichier réellement fourni par la boutique.
// L'icône iOS est déclarée en dur dans index.html (celle du site agence) :
// sans cette mise à jour, une boutique ajoutée à l'écran d'accueil d'un
// iPhone afficherait l'icône d'une AUTRE marque. Le serveur frontend fait
// déjà l'injection dans le HTML servi ; ceci couvre le cas où la boutique
// change d'icône pendant la session, sans rechargement.
function applyAppleTouchIcon(url) {
  if (!url) return;
  document.querySelectorAll("link[rel='apple-touch-icon']").forEach((el) => el.remove());
  const link = document.createElement("link");
  link.rel = "apple-touch-icon";
  link.href = url;
  document.head.appendChild(link);
}

function applyFavicon(url) {
  if (!url) return;
  document.querySelectorAll("link[rel~='icon']").forEach((el) => el.remove());
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = url;
  document.head.appendChild(link);
}

function fromApi(data) {
  return {
    name: data?.name || "ClickExpert",
    tagline: data?.tagline || "",
    display_name: data?.display_name || "",
    hide_name: data?.hide_name === true,
    logo_url: data?.logo_url || "",
    favicon_url: data?.icons?.favicon || data?.favicon_url || "",
    icons: data?.icons || {},
    loaded: true,
  };
}

// Cache module : survit aux navigations SPA, évite de refaire l'appel
// /theme/branding à chaque re-montage du provider. Réinitialisé au
// rechargement de page. Ignoré en mode Studio (preview via postMessage).
// Rempli d'avance par l'amorçage serveur (lib/bootstrap.js) quand le HTML
// le porte : le premier rendu affiche alors directement le nom et le logo
// de la boutique, jamais l'identité par défaut.
const _bootstrapped = bootstrapBranding();
let _cache = _bootstrapped ? fromApi(_bootstrapped) : null;

/** Nom de la boutique s'il est déjà connu (amorçage ou réponse de
 * /theme/branding), sinon chaîne vide. Lecture hors React (lib/seo.js). */
export function getBrandName() {
  return _cache?.name || "";
}

const BrandingContext = createContext(DEFAULTS);

export function BrandingProvider({ children }) {
  const preview = isStudioPreview();
  const [branding, setBranding] = useState(!preview && _cache ? _cache : DEFAULTS);

  useEffect(() => {
    if (!preview && _cache) return; // cache hit — pas d'appel réseau
    api.get("/theme/branding", { params: previewQueryParams() })
      .then(({ data }) => {
        const b = fromApi(data);
        if (!preview) _cache = b;
        setBranding(b);
      })
      .catch(() => {
        /* l'appel a échoué : on déverrouille avec les valeurs par défaut */
        setBranding((prev) => ({ ...prev, loaded: true }));
      });
  }, [preview]);

  useEffect(() => {
    // Brouillon non enregistré du Canvas du Studio de thème — voir
    // lib/studioPreviewBridge.js.
    if (!isStudioPreview()) return undefined;
    const onDraft = (e) => setBranding((prev) => ({ ...prev, ...e.detail }));
    window.addEventListener("studio:branding", onDraft);
    return () => window.removeEventListener("studio:branding", onDraft);
  }, []);

  useEffect(() => {
    applyFavicon(branding.favicon_url);
    applyAppleTouchIcon(branding.icons?.apple_touch || branding.favicon_url);
  }, [branding.favicon_url, branding.icons]);

  return <BrandingContext.Provider value={branding}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}
